// The boxes on a shipment's page. Each one takes the shipment's detail and shows its part.
import { Avatar, Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import { DataTable } from '../../components/DataTable';
import { StatusChip } from '../../components/StatusChip';
import { shortDate } from '../../lib/dates';
import { FileIcon } from '../documents/FileIcon';
import { DOC_STATUS_TONE, fileName, type DocRecord } from '../documents/sample';
import { EmptyState } from '../../components/EmptyState';
import { KeyValueList } from '../../components/KeyValueList';
import { Panel } from '../../components/Panel';
import type { Detail } from './sample';

export function StoryPanel({ d }: { d: Detail }) {
  return (
    <Panel title="Shipment Story">
      <Typography variant="body2" sx={{ display: 'flex', gap: 1 }}>
        <AutoAwesomeOutlined fontSize="small" color="primary" />
        <span>{d.story}</span>
      </Typography>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))' }}>
        {d.situation.map(([title, text]) => (
          <Card key={title} sx={{ bgcolor: 'action.hover' }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {text}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Panel>
  );
}

export function TimelinePanel({ d }: { d: Detail }) {
  return (
    <Panel title="Timeline">
      {d.timeline.length ? (
        <Stack spacing={1.5}>
          {d.timeline.map((e) => (
            <Stack key={e.when} direction="row" spacing={1.5}>
              <Box
                sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.9, flexShrink: 0 }}
              />
              <Box
                sx={{
                  display: 'grid',
                  gap: 1,
                  flex: 1,
                  minWidth: 0,
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.2fr) auto minmax(0, 1fr)' },
                  alignItems: 'start',
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {e.when}
                  </Typography>
                  <Typography sx={{ fontWeight: 650 }}>{e.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {e.place}
                  </Typography>
                </Box>
                <Box>
                  <Chip size="small" color="success" variant="outlined" label={e.tag} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {e.note}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      ) : (
        <EmptyState>No events yet.</EmptyState>
      )}
    </Panel>
  );
}

export function CargoPanel({ packages }: { packages: Detail['packages'] }) {
  const rows = packages.map(([dims, weight, what], i) => ({ n: String(i + 1), dims, weight, what }));
  return (
    <Panel title="Cargo Details">
      <DataTable
        rows={rows}
        rowKey={(r) => r.n}
        empty="No package list yet."
        columns={[
          { header: 'Package', primary: true, render: (r) => r.n },
          { header: 'Dimensions (cm)', render: (r) => r.dims },
          { header: 'Weight (kg)', render: (r) => r.weight },
          { header: 'Description', render: (r) => r.what },
        ]}
      />
    </Panel>
  );
}

export function RoutePanel({ route }: { route: Detail['route'] }) {
  return (
    <Panel title="Route & Schedule">
      <KeyValueList rows={route.map(([label, value]) => ({ label, value }))} />
    </Panel>
  );
}

export function KeyInfoPanel({ d }: { d: Detail }) {
  return (
    <Panel title="Key Information">
      <KeyValueList rows={d.keyInfo.map(([label, value]) => ({ label, value }))} />
    </Panel>
  );
}

export function PartiesPanel({ parties }: { parties: Detail['parties'] }) {
  return (
    <Panel title="Parties Involved">
      <Stack spacing={1.25}>
        {parties.map((p) => (
          <Stack key={p.role} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'action.selected', color: 'primary.main', fontWeight: 700 }}>
              {p.name[0]}
            </Avatar>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {p.role}
              </Typography>
              <Typography sx={{ fontWeight: 650, lineHeight: 1.25 }}>{p.name}</Typography>
              {p.place && (
                <Typography variant="caption" color="text.secondary">
                  {p.place}
                </Typography>
              )}
            </Box>
          </Stack>
        ))}
      </Stack>
    </Panel>
  );
}

/** The document records of one shipment (the files themselves are in Finder on the Mac) */
export function DocumentsPanel({
  records,
  title = 'Recent Documents',
  subtitle,
}: {
  records: DocRecord[];
  title?: string;
  subtitle?: string;
}) {
  return (
    <Panel title={title} subtitle={subtitle}>
      {records.length ? (
        <Stack spacing={1}>
          {records.map((r) => (
            <Stack key={r.id} direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
              <FileIcon ext={r.ext} />
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', fontWeight: 500 }}>
                {fileName(r)}
              </Typography>
              <StatusChip label={r.status} tone={DOC_STATUS_TONE[r.status]} />
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {shortDate(r.date)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : (
        <EmptyState>No documents yet.</EmptyState>
      )}
    </Panel>
  );
}
